import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControlStatus,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { IonCheckbox, IonIcon } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { provideComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { formatISO, subDays, addDays, parseISO } from 'date-fns';
import { distinctUntilChanged, map, startWith, Subscription, Observable, of } from 'rxjs';

import { NtyUniversalCreateComponent } from '@ninety/mobile/platform/api';
import {
  DescriptionComponent,
  NtyDatePickerComponent,
  NtyFormErrorComponent,
  NtyOptionComponent,
  NtySelectComponent,
  TeamMultiSelectComponent,
  TitleComponent,
  UserAvatarComponent,
} from '@ninety/mobile/platform/components';
import { UserNamePipe } from '@ninety/mobile/platform/services';
import {
  CurrentUserSelectors,
  CurrentUserTeamSelectors,
  LanguageSelectors,
  UniversalCreateActions,
  UserListSelectors,
} from '@ninety/mobile/platform/state';

import { CreateAssessmentForm } from '../../models/form/create-assessment-form.model';

import { CreateAssessmentDialogStore } from './_state/create-assessment-dialog.store';

@Component({
  selector: 'nty-create-assessment',
  templateUrl: './create-assessment.component.html',
  styleUrls: ['./create-assessment.component.scss'],
  imports: [
    CommonModule,
    IonCheckbox,
    IonIcon,
    ReactiveFormsModule,
    LetDirective,
    NtySelectComponent,
    NtyOptionComponent,
    DescriptionComponent,
    TitleComponent,
    UserAvatarComponent,
    TeamMultiSelectComponent,
    UserNamePipe,
    NtyDatePickerComponent,
    NtyFormErrorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [provideComponentStore(CreateAssessmentDialogStore)],
})
export class CreateAssessmentComponent implements NtyUniversalCreateComponent, OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly createAssessmentStore = inject(CreateAssessmentDialogStore);

  protected isSubmitted = false;

  minLaunchDate$: Observable<Date | string> = of(formatISO(new Date()));
  maxLaunchDate$: Observable<Date | string> = of(formatISO(new Date()));
  minEndDate$: Observable<Date | string> = of(formatISO(new Date()));

  vm$ = {
    language: this.store.select(LanguageSelectors.language),
    teams: this.store.select(CurrentUserTeamSelectors.selectTeams),
    assessmentDefinitions: this.createAssessmentStore.select(state => state.assessmentDefinitions),
    user: this.store.select(CurrentUserSelectors.selectUser),
    users: this.store.select(UserListSelectors.selectAll),
  };

  private readonly subscriptions = new Subscription();

  form = this.createAssessmentStore.form;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouchFn: () => void = () => {};
  private _touched = false;

  ngOnInit(): void {
    this.subscriptions.add(
      this.form.statusChanges
        .pipe(
          startWith('INVALID' as FormControlStatus),
          distinctUntilChanged(),
          map(status => status === 'VALID')
        )
        .subscribe(enable => this.store.dispatch(UniversalCreateActions.enable({ enable })))
    );

    this.subscriptions.add(
      this.store
        .select(this.createAssessmentStore.selectAssessmentsUniversalCreateButtonText)
        .pipe(distinctUntilChanged())
        .subscribe(createButtonText =>
          this.store.dispatch(UniversalCreateActions.createButtonText({ createButtonText }))
        )
    );
    // The launch date must be set to a value that is at least 1 day prior to the end date
    this.maxLaunchDate$ = this.form.controls.assessmentMetadata.controls.endDate.valueChanges.pipe(
      map(endDate => {
        if (endDate === null) return formatISO(new Date());
        return formatISO(subDays(typeof endDate === 'string' ? parseISO(endDate) : endDate, 1));
      })
    );

    // The end date must be set to a value that is at least 1 day after the launch date
    this.minEndDate$ = this.form.controls.assessmentMetadata.controls.launchDate.valueChanges.pipe(
      startWith(
        this.form.controls.assessmentMetadata.getRawValue().launchDate
          ? formatISO(this.form.controls.assessmentMetadata.getRawValue().launchDate ?? new Date())
          : formatISO(new Date())
      ),
      map(launchDate => {
        if (launchDate === null) return formatISO(addDays(new Date(), 1));
        return formatISO(addDays(typeof launchDate === 'string' ? parseISO(launchDate) : launchDate, 1));
      })
    );
  }

  // This method is called by the forms API on initialization to update the form model when values propagate from the view to the model.
  registerOnChange(fn: any): void {
    this.subscriptions.add(this.form.valueChanges.subscribe(fn));
  }

  // Registers a callback function that is called by the forms API on initialization to update the form model on blur.
  registerOnTouched(fn: any): void {
    this.onTouchFn = () => {
      if (this._touched === false) {
        this._touched = true;
        fn();
      }
    };
  }

  // Function that is called by the forms API when the control status changes to or from 'DISABLED'.
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  // This method is called by the forms API to write to the view when programmatic changes from model to view are requested.
  writeValue(formData: CreateAssessmentForm): void {
    const metadataControls = formData.assessmentMetadata.controls;

    this.form.patchValue(
      {
        assessmentMetadata: {
          assessmentDefinitionId: metadataControls.assessmentDefinitionId.value,
          name: metadataControls.name.value,
          description: metadataControls.description.value,
          launchDate: formatISO(metadataControls.launchDate.value ?? ''),
          endDate: metadataControls.endDate.value ? formatISO(metadataControls.endDate.value) : null,
        },
        teamIds: formData.teamIds.value,
        createTodos: formData.createTodos.value,
        anonymousReplies: formData.anonymousReplies.value,
        sendReportToUserIds: formData.sendReportToUserIds.value,
      },
      { emitEvent: false }
    );
  }

  // Method that performs synchronous validation against the provided control.
  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { assessmentMetadata: true };
  }

  create() {
    if (this.form.invalid) {
      this.isSubmitted = true;
      return;
    }
    this.createAssessmentStore.createCompanyAssessment();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected isOptional(formControl: AbstractControl): boolean {
    return !formControl.hasValidator(Validators.required) && !formControl.disabled;
  }

  protected hasRequiredError(formControl: AbstractControl | null | undefined): boolean {
    return formControl?.hasError('required') || false;
  }
}
